import { useEffect, useState } from "react";
import { TbSnowflake } from "react-icons/tb";

type Snowflake = {
    id: number;
    left: number;
    delay: number;
    duration: number;
    size: number;
    opacity: number;
    drift: number;
    spinDuration: number;
};

type SnowfallEffectProps = {
    /** Number of snowflakes to render */
    count?: number;
    /** Minimum size of snowflakes in pixels */
    minSize?: number;
    /** Maximum size of snowflakes in pixels */
    maxSize?: number;
    /** Speed multiplier for falling animation */
    speed?: number;
};

export const SnowfallEffect = ({ count = 50, minSize = 8, maxSize = 16, speed = 1 }: SnowfallEffectProps) => {
    const [snowflakes, setSnowflakes] = useState<Snowflake[]>([]);

    // Inject all keyframe animations
    useEffect(() => {
        if (typeof document === "undefined" || snowflakes.length === 0) {
            return;
        }

        const styleId = "snowfall-animations";
        let styleElement = document.getElementById(styleId);

        if (!styleElement) {
            styleElement = document.createElement("style");
            styleElement.id = styleId;
            document.head.appendChild(styleElement);
        }

        const keyframes = snowflakes
            .map(
                (snowflake) => `
            @keyframes snowfall-${snowflake.id} {
                0% {
                    transform: translateY(-100px) translateX(0);
                }
                50% {
                    transform: translateY(calc(50vh + 50px)) translateX(${snowflake.drift}px);
                }
                100% {
                    transform: translateY(calc(100vh + 100px)) translateX(${snowflake.drift * 1.5}px);
                }
            }
        `
            )
            .join("\n");

        styleElement.textContent = keyframes;

        return () => {
            const element = document.getElementById(styleId);
            if (element && snowflakes.length === 0) {
                element.remove();
            }
        };
    }, [snowflakes]);

    useEffect(() => {
        if (typeof window === "undefined") {
            return;
        }

        // Generate snowflakes with random properties
        const newSnowflakes: Snowflake[] = Array.from({ length: count }, (_, i) => ({
            id: i,
            left: Math.random() * 100, // Percentage from left
            delay: Math.random() * 5, // Random delay for staggered effect
            duration: (10 + Math.random() * 10) / speed, // Falling duration
            size: minSize + Math.random() * (maxSize - minSize),
            opacity: 0.4 + Math.random() * 0.6, // Varying opacity
            drift: (Math.random() - 0.5) * 30, // Horizontal drift in pixels
            spinDuration: 3 + Math.random() * 2, // Spin animation duration
        }));

        setSnowflakes(newSnowflakes);
    }, [count, minSize, maxSize, speed]);

    return (
        <div
            className="fixed inset-0 pointer-events-none overflow-hidden"
            style={{
                position: "fixed",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                zIndex: 9998,
                pointerEvents: "none",
            }}
        >
            {snowflakes.map((snowflake) => (
                <div
                    key={snowflake.id}
                    className="absolute top-0 text-white/80"
                    style={{
                        left: `${snowflake.left}%`,
                        fontSize: `${snowflake.size}px`,
                        opacity: snowflake.opacity,
                        animation: `snowfall-${snowflake.id} ${snowflake.duration}s linear ${snowflake.delay}s infinite`,
                        filter: "drop-shadow(1px 1px 2px rgba(0, 0, 0, 0.5))",
                    }}
                >
                    <TbSnowflake
                        style={{
                            animation: `snowflake-spin ${snowflake.spinDuration}s linear infinite`,
                            display: "block",
                        }}
                    />
                </div>
            ))}
        </div>
    );
};
